import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";

import { updateCallPtaSchema, UpdateCallPtaSchemaInput, UpdateCallPtaSchemaOutput } from "view/calls/utils/call.schema";

import { CallTimeField } from "__generated__/enums";
import { UpdateCallTimeDocument } from "__generated__/view/calls/graphql/call.mutations.generated";

const UpdateCallPtaDialog = ({ open, onClose, call, refetch }: {
	open: boolean,
	onClose: () => void,
	call: {
		id: number;
		pta: number | null;
	}
	refetch: () => void,
}) => {

	const { control, handleSubmit, reset } = useForm<UpdateCallPtaSchemaInput, unknown, UpdateCallPtaSchemaOutput>({
		resolver: zodResolver(updateCallPtaSchema),
		defaultValues: {
			pta: null,
		},
	});

	const handleOnClose = () => {
		onClose();
		reset({
			pta: null,
		});
	};

	const [ updateCallTime, { loading } ] = useMutation(UpdateCallTimeDocument);

	const save = (data: UpdateCallPtaSchemaOutput) => {
		updateCallTime({
			variables: {
				id: call.id,
				time: data.pta,
				field: CallTimeField.Pta,
			},
		}).then(() => {
			refetch();
			handleOnClose();
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				pta: call.pta,
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CALLS_PTA" })}
			form='updateCallPtaForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="updateCallPtaForm">
				<FormGroup label={intl.formatMessage({ id: "CALLS_PTA" })} overflow="hidden" required>
					<Controller
						name="pta"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker { ...field } showTimeInput />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default UpdateCallPtaDialog;
